"use client";

import React from "react";
import { languageCountryCodes } from "modules/common/model/models";
import { cn } from "utils/utils";

interface LanguageFlagProps {
  language: string;
  className?: string;
  size?: "xs" | "sm" | "md";
  framed?: boolean;
}

const LanguageFlag: React.FC<LanguageFlagProps> = ({
  language,
  className,
  size = "md",
  framed = false,
}) => {
  const sizeClass = framed
    ? size === "xs"
      ? "h-3 w-5"
      : size === "sm"
        ? "h-4 w-6"
        : "h-5 w-7"
    : size === "xs"
      ? "h-4 w-4"
      : size === "sm"
        ? "h-5 w-5"
        : "h-6 w-6";
  const frameClass =
    framed && "ring-base-content/30 rounded-[2px] ring-1 shadow-sm";

  // Handle special cases
  if (language.toLowerCase() === "phyrexian") {
    if (framed) {
      return (
        <div
          className="tooltip tooltip-secondary flex items-center justify-center"
          data-tip={language}
        >
          <span
            className={cn(
              "flex items-center justify-center",
              sizeClass,
              frameClass
            )}
          >
            <i
              className={cn(
                "ss ss-nph ss-fw text-secondary text-xs",
                className
              )}
            />
          </span>
        </div>
      );
    }

    return (
      <div className="text-center">
        <div className="tooltip tooltip-secondary" data-tip={language}>
          <i
            className={cn(
              "ss ss-nph ss-fw",
              "text-secondary inline-block",
              sizeClass,
              className
            )}
          />
        </div>
      </div>
    );
  }

  const getCountryCode = (lang: string) => {
    const lowercaseLang = lang.toLowerCase();
    if (lowercaseLang === "ancient greek") {
      return "gr";
    }
    if (lowercaseLang === "sanskrit") {
      return "in";
    }
    if (lowercaseLang === "latin") {
      return "va";
    }
    return languageCountryCodes[
      lang as keyof typeof languageCountryCodes
    ]?.toLowerCase();
  };

  const countryCode = getCountryCode(language);

  return (
    <div
      className="tooltip tooltip-secondary flex items-center justify-center"
      data-tip={language}
    >
      <div className={cn("relative overflow-hidden", sizeClass, frameClass)}>
        {/* eslint-disable-next-line @next/next/no-img-element -- SVGs from external CDN don't benefit from next/image optimization */}
        <img
          src={`https://flagcdn.com/${countryCode}.svg`}
          alt={language}
          className={cn(
            "h-full w-full",
            framed ? "rounded-[1px] object-cover" : "object-contain",
            className
          )}
        />
      </div>
    </div>
  );
};

export default LanguageFlag;
